import React, { useState } from 'react';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  BADGE_BRAND, BTN_GHOST, CHANGED, FIELD_SM, HINT, ICON_BTN, INVALID, MoneyInput, euro, sameName,
} from './parts';

/**
 * Πίνακας γραμμών «όνομα · ποσό · προεπιλογή» — οι επιλογές μιας ομάδας επιλογών και τα μεγέθη μιας
 * ομάδας μεγεθών. Πρόχειρο μέχρι την «Αποθήκευση» της κάρτας: εδώ μόνο αλλάζει η λίστα.
 * Σειρά: σύρσιμο από τη λαβή ή ↑/↓ με εστίαση στη λαβή (η σειρά του ταμείου).
 */

export interface RowDraft {
  /** Σταθερό κλειδί: `c<id>` για ό,τι υπάρχει, `n<αύξων>` για νέα γραμμή. */
  key: string;
  id: number | null;
  name: string;
  /** null = κενό ή άκυρο ποσό (η αποθήκευση περιμένει). */
  price: number | null;
  is_default: boolean;
}

export interface RowsEditorLabels {
  nameCol: string;
  priceCol: string;
  defaultCol: string;
  add: string;
  namePlaceholder: string;
  nameAria: (n: number) => string;
  priceAria: (name: string) => string;
  defaultAria: (name: string) => string;
  removeAria: (name: string) => string;
  moveAria: (name: string) => string;
}

export const rowKeyOf = (id: number) => `c${id}`;

let newRowSeq = 0;
export const newRowKey = () => { newRowSeq += 1; return `n${newRowSeq}`; };

const GRID = 'grid grid-cols-[14px_minmax(0,1fr)_96px_44px_28px] items-center gap-x-2';

export interface RowError { field: 'name' | 'price'; message: string }

/** Τι λείπει ή συγκρούεται ανά γραμμή (κλειδί → λάθος). */
export function validateRows(
  rows: RowDraft[],
  saved: Map<string, RowDraft>,
  opts: { allowNegative: boolean; messages: { nameRequired: string; duplicate: (name: string) => string; priceInvalid: string; priceNegative: string } },
): Record<string, RowError> {
  const out: Record<string, RowError> = {};
  rows.forEach((row, i) => {
    const name = row.name.trim();
    if (!name) { out[row.key] = { field: 'name', message: opts.messages.nameRequired }; return; }
    if (rows.some((other, j) => j < i && sameName(other.name, name))) {
      out[row.key] = { field: 'name', message: opts.messages.duplicate(name) };
      return;
    }
    if (row.price == null) { out[row.key] = { field: 'price', message: opts.messages.priceInvalid }; return; }
    // Ε13α: παλιά αρνητική τιμή που δεν άλλαξε περνά (ο server τη δέχεται ίδια)· νέα αρνητική όχι.
    const before = saved.get(row.key);
    if (!opts.allowNegative && row.price < 0 && (!before || before.price !== row.price)) {
      out[row.key] = { field: 'price', message: opts.messages.priceNegative };
    }
  });
  return out;
}

export const RowsEditor: React.FC<{
  rows: RowDraft[];
  saved: Map<string, RowDraft>;
  onChange: (rows: RowDraft[]) => void;
  /** «Μία»: το πολύ μία προεπιλογή (πάτημα ξανά = καμία). «Πολλές»: όσες θέλεις. */
  exclusiveDefault: boolean;
  labels: RowsEditorLabels;
  errors: Record<string, RowError>;
  /** Να φαίνονται τα λάθη (μετά από απόπειρα αποθήκευσης). */
  showErrors: boolean;
  hint?: React.ReactNode;
  /** Ποσό νέας γραμμής: 0 στις επιλογές (χωρίς χρέωση), κενό στα μεγέθη — μέγεθος 0 € = δωρεάν είδος. */
  newRowPrice?: number | null;
}> = ({ rows, saved, onChange, exclusiveDefault, labels, errors, showErrors, hint, newRowPrice = 0 }) => {
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);

  const update = (key: string, patch: Partial<RowDraft>) => onChange(rows.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key: string) => onChange(rows.filter(r => r.key !== key));
  const add = () => {
    const key = newRowKey();
    setFocusKey(key);
    onChange([...rows, { key, id: null, name: '', price: newRowPrice, is_default: false }]);
  };
  const move = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) return;
    const next = rows.slice();
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    onChange(next);
  };
  const toggleDefault = (key: string) => {
    const row = rows.find(r => r.key === key);
    if (!row) return;
    const on = !row.is_default;
    onChange(rows.map(r => {
      if (r.key === key) return { ...r, is_default: on };
      return exclusiveDefault && on ? { ...r, is_default: false } : r;
    }));
  };

  return (
    <div className="flex flex-col gap-2 min-w-0">
      <div className="rounded-xl border border-line bg-surface-2 dark:bg-surface-1 px-2.5 pt-2.5 pb-1">
        <div className={`${GRID} pb-1.5 text-[11.5px] font-semibold text-t3`}>
          <span />
          <span>{labels.nameCol}</span>
          <span className="pr-6 text-right">{labels.priceCol}</span>
          <span className="text-center">{labels.defaultCol}</span>
          <span />
        </div>
        {rows.map((row, index) => {
          const before = saved.get(row.key);
          const isNew = !before;
          const shown = row.name.trim() || labels.nameAria(index + 1);
          const err = showErrors ? errors[row.key] : undefined;
          return (
            <div
              key={row.key}
              data-testid={`row-${row.key}`}
              onDragOver={e => { if (dragKey) e.preventDefault(); }}
              onDrop={e => {
                e.preventDefault();
                if (dragKey) move(rows.findIndex(r => r.key === dragKey), index);
                setDragKey(null);
              }}
              className={`border-t border-line py-1 ${dragKey === row.key ? 'opacity-50' : ''}`}
            >
              <div className={GRID}>
                <button
                  type="button"
                  draggable
                  aria-label={labels.moveAria(shown)}
                  title={labels.moveAria(shown)}
                  onDragStart={e => {
                    setDragKey(row.key);
                    try { e.dataTransfer.setData('text/plain', row.key); e.dataTransfer.effectAllowed = 'move'; } catch { /* jsdom */ }
                  }}
                  onDragEnd={() => setDragKey(null)}
                  onKeyDown={e => {
                    if (e.key === 'ArrowUp') { e.preventDefault(); move(index, index - 1); }
                    if (e.key === 'ArrowDown') { e.preventDefault(); move(index, index + 1); }
                  }}
                  className="grid h-7 w-3.5 cursor-grab place-items-center rounded text-t4 hover:text-t2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <GripVertical className="h-[13px] w-[13px]" aria-hidden="true" />
                </button>
                <input
                  type="text"
                  value={row.name}
                  maxLength={100}
                  autoFocus={focusKey === row.key}
                  placeholder={labels.namePlaceholder}
                  aria-label={labels.nameAria(index + 1)}
                  aria-invalid={err?.field === 'name' ? true : undefined}
                  onChange={e => update(row.key, { name: e.target.value })}
                  className={`${FIELD_SM} ${isNew ? 'border-dashed' : ''} ${isNew || (before && before.name !== row.name) ? CHANGED : ''} ${err?.field === 'name' ? INVALID : ''}`}
                />
                <MoneyInput
                  value={row.price}
                  onChange={price => update(row.key, { price })}
                  label={labels.priceAria(shown)}
                  changed={isNew || (!!before && before.price !== row.price)}
                  invalid={err?.field === 'price'}
                />
                <span className="grid place-items-center">
                  <button
                    type="button"
                    aria-pressed={row.is_default}
                    aria-label={labels.defaultAria(shown)}
                    title={labels.defaultAria(shown)}
                    onClick={() => toggleDefault(row.key)}
                    className={`grid h-7 w-7 place-items-center rounded-lg hover:bg-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                      before && before.is_default !== row.is_default ? 'ring-2 ring-amber-500' : ''
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`relative block h-4 w-4 border-[1.5px] ${exclusiveDefault ? 'rounded-full' : 'rounded-[4.5px]'} ${
                        row.is_default ? 'border-brand' : 'border-line-strong bg-white dark:bg-surface-2'
                      } ${row.is_default && !exclusiveDefault ? 'bg-brand' : ''}`}
                    >
                      {row.is_default && exclusiveDefault && <span className="absolute inset-[2.5px] rounded-full bg-brand" />}
                      {row.is_default && !exclusiveDefault && (
                        <svg viewBox="0 0 24 24" className="absolute inset-0 m-auto h-3 w-3 text-white" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      )}
                    </span>
                  </button>
                </span>
                <button type="button" onClick={() => remove(row.key)} aria-label={labels.removeAria(shown)} title={labels.removeAria(shown)} className={ICON_BTN}>
                  <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
                </button>
              </div>
              {err && <p className="pl-[22px] pt-1 text-xs text-red-700 dark:text-red-300">{err.message}</p>}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button type="button" onClick={add} className={BTN_GHOST}>
          <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
          {labels.add}
        </button>
        {hint && <span className={HINT}>{hint}</span>}
      </div>
    </div>
  );
};

/** Το ίδιο, μόνο για ανάγνωση (κεντρικός κατάλογος). */
export const RowsReadOnly: React.FC<{
  rows: Array<{ key: string; name: string; price: number; is_default: boolean }>;
  labels: Pick<RowsEditorLabels, 'nameCol' | 'priceCol' | 'defaultCol'>;
  emptyText: string;
  defaultBadge: string;
}> = ({ rows, labels, emptyText, defaultBadge }) => (
  <div className="rounded-xl border border-line bg-surface-2 dark:bg-surface-1 px-3 py-2">
    {rows.length === 0 ? (
      <p className="py-2 text-[13px] text-t4">{emptyText}</p>
    ) : (
      <table className="w-full text-[13.5px]">
        <thead>
          <tr className="text-left text-[11.5px] font-semibold text-t3">
            <th className="pb-1.5 font-semibold">{labels.nameCol}</th>
            <th className="pb-1.5 text-right font-semibold">{labels.priceCol}</th>
            <th className="w-16 pb-1.5 text-center font-semibold"><span className="sr-only">{labels.defaultCol}</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} className="border-t border-line">
              <td className="py-1.5 text-t1">{r.name}</td>
              <td className="py-1.5 text-right font-semibold tabular-nums text-t1">{euro(r.price)}</td>
              <td className="py-1.5 text-center">{r.is_default && <span className={BADGE_BRAND}>{defaultBadge}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);
